import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Modal, Popconfirm, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { Table } from "antd";
import images from "../../../themes/appImage";
import { capitalizeFirstLetters, debounce } from "../../../utils/global-func";
import { emptySpaceInputHandler } from "../../../utils";
import {
  createDepartmentsLoad,
  departmentsDeleteLoad,
  departmentsListLoad,
} from "../../../redux/slices/department.slice";
import { useFormik } from "formik";
import * as Yup from "yup";

const numberOfRowOptionList = [
  { value: "one", label: 10 },
  { value: "two", label: 20 },
  { value: "three", label: 25 },
];

let LIST_LIMIT = 10;
let OFFSET = 0;

const Departments = ({ myid }) => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const { departmentListData, departmentsListPaginationData } = useSelector(
    (state) => state.departmentsReducer
  );

  const deleteItemSuccess = () => {
    const data = {
      offset:
        departmentListData?.length === 1 && OFFSET > 1 ? OFFSET - 10 : OFFSET,
      limit: LIST_LIMIT,
      search: search,
      orgId: myid,
    };
    OFFSET -= 10;
    dispatch(departmentsListLoad(data));
  };

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "srno",
      key: "srno",
      sorter: false,
      width: "5%",
      render: (value, item, index) => OFFSET + index + 1,
    },
    {
      title: "Id",
      dataIndex: "_id",
      key: "_id",
    },
    {
      title: "Department",
      dataIndex: "Department",
      key: "Department",
      title: (v, r, i) => {
        return (
          <div className="sort-icons">
            <span>Departments</span>
          </div>
        );
      },
      render: (value, item, index) => (
        <div className="organization-image-sec">
          <div className="flex items-center justify-center gap-4">
            <div>{capitalizeFirstLetters(item?.name)}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      align: "center",
      render: (text, record) => (
        <div className="flex justify-evenly">
          <Popconfirm
            title="Are you sure to delete this department?"
            onConfirm={() =>
              dispatch(
                departmentsDeleteLoad({
                  id: record._id,
                  onSuccess: deleteItemSuccess,
                })
              )
            } // Use _id instead of key
            okText="Yes"
            cancelText="No"
          >
            <div className="pl-[25px]">
              <img src={images.trash} alt="trash" />
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Please enter department name")
      .max(30, "Department name must be at most 30 characters"),
  });

  const addItemSuccess = () => {
    const data = {
      offset: OFFSET,
      limit: LIST_LIMIT,
      search: search,
      orgId: myid,
    };
    dispatch(departmentsListLoad(data));
    setIsSecondModalOpen(false);
    formik.resetForm();
  };

  const formik = useFormik({
    initialValues: {
      name: "",
    },
    validationSchema,
    onSubmit: (values) => {
      const apiPayload = {
        name: values.name,
        orgId: myid,
      };
      dispatch(
        createDepartmentsLoad({
          payload: apiPayload,
          onSuccess: addItemSuccess,
        })
      );
    },
  });

  const inputHandler = (event) => {
    let { name } = event.target;
    formik.setFieldValue(name, emptySpaceInputHandler(event));
  };

  useEffect(() => {
    const data = {
      offset: 0,
      limit: LIST_LIMIT,
      search: search,
      orgId: myid,
    };
    dispatch(departmentsListLoad(data));
  }, []);

  // P A G I N A T I O N

  // --S T A T E--
  const START = useMemo(() => OFFSET + 1, [departmentListData]);
  const TOTAL_LIST_ITEMS = useMemo(
    () => departmentsListPaginationData?.totalCount,
    [departmentListData]
  );
  const END = useMemo(
    () => Math.min(OFFSET + LIST_LIMIT, TOTAL_LIST_ITEMS),
    [departmentListData]
  );
  const canCallNextData = OFFSET + LIST_LIMIT < TOTAL_LIST_ITEMS;
  // --A C T I O N S--
  const paginationHandler = useCallback(
    (type) => {
      if (type === "pre") {
        OFFSET = OFFSET - LIST_LIMIT;
        const data = {
          offset: OFFSET,
          limit: LIST_LIMIT,
          search: search,
          orgId: myid,
        };
        dispatch(departmentsListLoad(data));
      } else {
        OFFSET = OFFSET + LIST_LIMIT;
        const data = {
          offset: OFFSET,
          limit: LIST_LIMIT,
          search: search,
          orgId: myid,
        };
        dispatch(departmentsListLoad(data));
      }
    },
    [departmentListData]
  );

  const numberOfRowHandler = useCallback(
    (ddateStringate, dataObj) => {
      LIST_LIMIT = dataObj.label;
      OFFSET = 0;
      const data = {
        offset: OFFSET,
        limit: LIST_LIMIT,
        search: search,
        orgId: myid,
      };
      dispatch(departmentsListLoad(data));
    },
    [departmentListData]
  );

  const searchHandler = useCallback(
    (e) => {
      if (emptySpaceInputHandler(e) || e.target.value.length == "") {
        LIST_LIMIT = 10;
        OFFSET = 0;
        setSearch(emptySpaceInputHandler(e));
        const data = {
          offset: 0,
          limit: 10,
          orgId: myid,
          search: emptySpaceInputHandler(e).trim(),
        };
        debounce(() => dispatch(departmentsListLoad(data)));
      }
    },
    [departmentListData]
  );

  const [isSecondModalOpen, setIsSecondModalOpen] = useState(false);

  const addDepModalHandler = () => {
    formik.resetForm();
    setIsSecondModalOpen(!isSecondModalOpen);
  };

  return (
    <div>
      <div className="site-layout-background" style={{ minHeight: 360 }}>
        <div className="top-header-organi-zations flex flex-row justify-around">
          <div className="organi-zations-heading-text ml-3">
            <h1>Departments</h1>
          </div>
          <div className="mr-3 w-[143px] h-[48px] py-[12px] px-[24px] bg-purple-gradient rounded-[56px] flex justify-center items-center shadow-button-shadow text-white">
            <button onClick={addDepModalHandler}>
              <span className="'text-white text-[14px] font-semibold">
                Add New
              </span>
            </button>
          </div>
        </div>

        <div className="content-e">
          <div class="bg-gradient-primary  system-line">
            <input
              type="text"
              value={search}
              onChange={searchHandler}
              className="iocn-search  search-bar "
              placeholder="Search departments"
            />
          </div>

          <Table
            className="table-wrapper"
            dataSource={departmentListData}
            columns={columns}
            showSorterTooltip={false}
            pagination={{
              current: 0,
              pageSize: 0,
              total: TOTAL_LIST_ITEMS,
            }}
          />

          {TOTAL_LIST_ITEMS > 10 && (
            <div className="footer-section">
              <div className="foot-main-div">
                <div className="rows">
                  <div className="row-per-page">
                    <p>Rows per page</p>
                  </div>
                  <div className="top-checkbox fot-select">
                    <Select
                      defaultValue={LIST_LIMIT}
                      onChange={numberOfRowHandler}
                      options={numberOfRowOptionList}
                    />
                  </div>
                </div>

                <div className="page-count">
                  <p>{`${START}-${END} of ${TOTAL_LIST_ITEMS}`}</p>
                </div>

                <div className="back-and-next">
                  <span
                    onClick={() => OFFSET !== 0 && paginationHandler("pre")}
                  >
                    <img
                      src={OFFSET == 0 ? images.per : images.paginationDimLeft}
                      style={{ tintColor: "red" }}
                      alt="back"
                    />
                  </span>
                  <span
                    onClick={() => canCallNextData && paginationHandler("next")}
                  >
                    <img
                      src={
                        canCallNextData == false
                          ? images.paginationDimRight
                          : images.new
                      }
                      alt="back"
                    />
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Modal
        title={
          <div className="text-3xl text-dark mt-6">Add New Departments</div>
        }
        visible={isSecondModalOpen}
        onOk={addDepModalHandler}
        onCancel={addDepModalHandler}
        footer={null}
        centered
        className="departmentModal"
      >
        <form onSubmit={formik.handleSubmit}>
          <div className="create-org-detail">
            <div className="orgn-plans">
              <div className="modal-right-side">
                <div className="form-group mt-[-20px]">
                  <label>Department Name</label>

                  <input
                    className="form-control mt-2"
                    type="text"
                    placeholder="Enter department name"
                    name="name"
                    value={formik.values.name}
                    onChange={inputHandler}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.name && formik.errors.name ? (
                    <div className="text-red-500 text-xs mt-1 font-normal">
                      {formik.errors.name}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <div className="click-to-upload top-border-line">
            <div className="create-inner-circle-btn button-spacing">
              <button type="submit">Create Department</button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};
export default Departments;
